/**
 * Lock screen, notification shade, headphone buttons, and car head units.
 *
 * Without this, a backgrounded PWA plays audio with no way to control it — which
 * is the single biggest thing that makes a web music player feel broken next to a
 * native one.
 */

/** The least a thing needs for the lock screen. A queue item satisfies it; so
 *  does a preview, which is not a queue item and should not pretend to be. */
export interface NowPlayingLike {
  title: string
  artistName: string
  albumName?: string | null
  coverUrl?: string | null
}

export interface MediaSessionHandlers {
  play: () => void
  pause: () => void
  previous: () => void
  next: () => void
  seekTo: (seconds: number) => void
  stop: () => void
}

function supported(): boolean {
  return typeof navigator !== 'undefined' && 'mediaSession' in navigator
}

export function setMediaMetadata(item: NowPlayingLike | null): void {
  if (!supported()) return

  if (!item) {
    navigator.mediaSession.metadata = null
    return
  }

  // Artwork must be reachable by the OS media service. Same-origin cover URLs
  // work; a remote provider URL is passed through as-is and simply may not load.
  //
  // Largest first, and that ordering is the whole point. All three entries are
  // the same image — the sizes are a hint, not a promise — and iOS has a long
  // history of simply taking the first entry. With 96x96 leading, a car head
  // unit rendered a thumbnail on a display the size of a paperback.
  const artwork: MediaImage[] = item.coverUrl
    ? [
        { src: item.coverUrl, sizes: '512x512', type: 'image/jpeg' },
        { src: item.coverUrl, sizes: '256x256', type: 'image/jpeg' },
        { src: item.coverUrl, sizes: '96x96', type: 'image/jpeg' },
      ]
    : []

  navigator.mediaSession.metadata = new MediaMetadata({
    title: item.title,
    artist: item.artistName,
    album: item.albumName ?? '',
    artwork,
  })
}

export function setPlaybackState(isPlaying: boolean): void {
  if (!supported()) return
  navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused'
}

export function setPositionState(currentTime: number, duration: number, rate = 1): void {
  if (!supported() || typeof navigator.mediaSession.setPositionState !== 'function') return
  // Throws on nonsense values (duration 0, position past the end), which happens
  // routinely between loading a track and its metadata arriving.
  if (!Number.isFinite(duration) || duration <= 0) return
  try {
    navigator.mediaSession.setPositionState({
      duration,
      playbackRate: rate,
      position: Math.max(0, Math.min(currentTime, duration)),
    })
  } catch {
    /* a stale position during a track change is not worth reporting */
  }
}

export function registerMediaHandlers(handlers: MediaSessionHandlers): () => void {
  if (!supported()) return () => {}

  const bindings: Array<[MediaSessionAction, MediaSessionActionHandler]> = [
    ['play', () => handlers.play()],
    ['pause', () => handlers.pause()],
    ['previoustrack', () => handlers.previous()],
    ['nexttrack', () => handlers.next()],
    ['stop', () => handlers.stop()],
    [
      'seekto',
      (details) => {
        if (typeof details.seekTime === 'number') handlers.seekTo(details.seekTime)
      },
    ],
    // Deliberately no seekbackward/seekforward.
    //
    // Platforms give a media session a limited number of control slots, and on
    // iOS registering the skip-by-seconds actions *replaces* the next/previous
    // track buttons. That is the right trade for a podcast app and the wrong
    // one for a music player: it turns the steering-wheel skip buttons in a car
    // into ten-second scrubs, which is precisely the control you most want
    // while driving. `seekto` stays, so the scrubber on the lock screen and the
    // head unit's progress bar both still work.
  ]

  const registered: MediaSessionAction[] = []
  for (const [action, handler] of bindings) {
    try {
      navigator.mediaSession.setActionHandler(action, handler)
      registered.push(action)
    } catch {
      // Not every browser implements every action; an unsupported one throws.
    }
  }

  return () => {
    for (const action of registered) {
      try {
        navigator.mediaSession.setActionHandler(action, null)
      } catch {
        /* ignore */
      }
    }
  }
}
